import { Button, PopupMenu, PopupMenuButton, PopupMenuContainer, usePopupMenu } from '@components'
import { TranslatedInput } from '@components/TranslatedInput'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaPen } from 'react-icons/fa'
import styled from 'styled-components'
import { IAccount } from '@query'
import { IUpdateTabularAccountProps } from '@pages/AccountsPage/sections'
import { StyledCell } from './EditableCell'

interface Props {
  value: any
  updateData: (data: IUpdateTabularAccountProps) => Promise<any>
  row: {
    original: IAccount
  }
  name: string
  label?: string
  info?: string
  disabled?: boolean
}

export const EditableTranslatedCell: React.FC<Props> = ({
  label,
  info,
  value,
  name,
  row: { original },
  updateData,
  disabled = false
}) => {
  const _updateData = (newValue: any) =>
    updateData({
      accountId: original.id,
      data: {
        [name]: newValue
      }
    })

  const handleKeyUp = e => {
    if (e.key === 'Enter' && !disabled) {
      // Simulate mousedown to trigger useOnClickOutside
      const clickEvent = new MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true
      })
      e.currentTarget.dispatchEvent(clickEvent)

      // Open dropdown by click
      e.currentTarget.click()
    }
  }

  return (
    <PopupMenuContainer>
      <PopupMenuButton>
        <StyledCell tabIndex={disabled ? -1 : 0} onKeyUp={handleKeyUp} $disabled={disabled}>
          <span>{original.name}</span>
          {!disabled && <FaPen />}
        </StyledCell>
      </PopupMenuButton>

      {!disabled && (
        <PopupMenu menuGrow="left">
          <PopupContent>
            <EditForm label={label} info={info} initialValue={value} onUpdate={_updateData} />
          </PopupContent>
        </PopupMenu>
      )}
    </PopupMenuContainer>
  )
}

interface EditFormProps {
  label: string
  info: string
  initialValue: any
  onUpdate: (value: any) => Promise<any>
}

const EditForm: React.FC<EditFormProps> = ({ label, info, initialValue, onUpdate }) => {
  const [t] = useTranslation()
  const { toggleVisibility } = usePopupMenu()
  const {
    handleSubmit,
    control,
    formState: { isSubmitting }
  } = useForm({
    defaultValues: {
      edit: initialValue
    }
  })

  const onSubmit = handleSubmit(async data => {
    try {
      await onUpdate(data.edit)
      toggleVisibility(false)
    } catch {
      return null
    }
  })

  return (
    <form onSubmit={onSubmit}>
      <Controller
        name={'edit'}
        control={control}
        render={({ field }) => (
          <TranslatedInput
            name={'translated_name'}
            value={field.value}
            onChange={field.onChange}
            label={label}
            info={info}
          />
        )}
      />

      <ButtonContainer>
        <Button icon={<FaCheck />} isSecondary={true} intent="success" showSpinner={isSubmitting}>
          {t('general.save')}
        </Button>
      </ButtonContainer>
    </form>
  )
}

const PopupContent = styled.div`
  padding: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 300px;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
`
